import React, { useRef } from 'react';
import { Modal } from 'antd';
import { message } from '../utils/antdMessage';
import apiFetch from '../utils/apiFetch';
import { toMapLngLat } from '../utils/dashboard/basemapCoord';
import { removeAllOrthoGlbOverlays } from '../utils/orthoGlbMapLayer';
import { isValidWgs84Gps } from '../utils/exifGps';
import {
  PHOTO_DENSITY_SOURCE,
  PHOTO_DENSITY_LAYER,
  PHOTO_CLUSTER_COLORS
} from '../utils/orthoToolbox';
import orthoReadJson from './orthoReadJson';

/**
 * 航片密度预览、飞行范围与渲染区框选
 */
export default function useOrthoPhotoDensity({
  getMap,
  mapReady,
  onStartInteract,
  setBusy,
  setOrthoManageOpen,
  setGlbMapActive,
  setBlockEarthBlankFly,
  beginBboxSelect,
  clearBoxPreview,
  setSelectMode,
  beginAoiInteract,
  endAoiInteractSoon,
  activeId,
  activeTask,
  loadTasks
}) {
  /** 最近一次密度预览的飞行范围（优先主团），框选渲染区时自动 flyTo */
  const photoRenderBoundsRef = useRef(null);

  const clearPhotoDensityLayer = () => {
    const map = getMap?.();
    if (!map) return;
    try {
      if (map.getLayer(PHOTO_DENSITY_LAYER)) map.removeLayer(PHOTO_DENSITY_LAYER);
      if (map.getSource(PHOTO_DENSITY_SOURCE)) map.removeSource(PHOTO_DENSITY_SOURCE);
    } catch (_) { /* ignore */ }
  };

  /** 退出地球球体投影，否则 fitBounds 容易落到错误视野 */
  const prepareFlatMapForPhotoFly = (map) => {
    if (!map) return;
    setBlockEarthBlankFly(true);
    try { map.stop?.(); } catch (_) { /* ignore */ }
    try {
      const proj = map.getProjection?.();
      const name = typeof proj === 'string' ? proj : proj?.name;
      if (name === 'globe' && typeof map.setProjection === 'function') {
        map.setProjection('mercator');
      }
    } catch (_) { /* ignore */ }
    try {
      map.setPitch?.(0);
      map.setBearing?.(0);
    } catch (_) { /* ignore */ }
  };

  /** 框选前清掉 3D 黑画布，避免底图被盖成「一片空白」 */
  const ensureMapVisibleForSelect = () => {
    const map = getMap?.();
    if (!map) return null;
    try {
      removeAllOrthoGlbOverlays(map);
      setGlbMapActive({});
    } catch (_) { /* ignore */ }
    setBlockEarthBlankFly(true);
    return map;
  };

  /** 从任务已保存的 renderAoi（WGS84）得到显示坐标 bounds */
  const boundsFromSavedAoi = (map) => {
    const aoi = activeTask?.renderAoi;
    if (!aoi) return null;
    const w = Number(aoi.west);
    const s = Number(aoi.south);
    const e = Number(aoi.east);
    const n = Number(aoi.north);
    if (![w, s, e, n].every(Number.isFinite) || !(w < e && s < n)) return null;
    const sw = toMapLngLat(w, s, undefined, map) || [w, s];
    const ne = toMapLngLat(e, n, undefined, map) || [e, n];
    const west = Math.min(sw[0], ne[0]);
    const east = Math.max(sw[0], ne[0]);
    const south = Math.min(sw[1], ne[1]);
    const north = Math.max(sw[1], ne[1]);
    if (!(west < east && south < north)) return null;
    return { west, south, east, north };
  };

  /** WGS84 bounds → 当前底图显示坐标 bounds */
  const mapBoundsFromWgs = (map, wgsBounds) => {
    if (!wgsBounds) return null;
    const { west, south, east, north } = wgsBounds;
    if (![west, south, east, north].every(Number.isFinite)) return null;
    const sw = toMapLngLat(west, south, undefined, map) || [west, south];
    const ne = toMapLngLat(east, north, undefined, map) || [east, north];
    const b = {
      west: Math.min(sw[0], ne[0]),
      south: Math.min(sw[1], ne[1]),
      east: Math.max(sw[0], ne[0]),
      north: Math.max(sw[1], ne[1])
    };
    // 单点/重合时扩一点，避免 fitBounds 拒绝
    if (!(b.west < b.east)) {
      b.west -= 0.002;
      b.east += 0.002;
    }
    if (!(b.south < b.north)) {
      b.south -= 0.002;
      b.north += 0.002;
    }
    return b;
  };

  /** 飞到渲染相关区域（优先主团 / 已存 bounds / 已保存选区） */
  const flyToPhotoRenderArea = (map, opts = {}) => {
    const m = map || getMap?.();
    if (!m) return false;
    const b = opts.bounds || photoRenderBoundsRef.current || boundsFromSavedAoi(m);
    if (!b) return false;
    let { west, south, east, north } = b;
    if (![west, south, east, north].every(Number.isFinite)) return false;
    // 拒绝 Null Island（无 GPS 误当成 0,0 时会飞非洲）
    const cx = (west + east) / 2;
    const cy = (south + north) / 2;
    if (Math.abs(cx) < 0.05 && Math.abs(cy) < 0.05) {
      console.warn('[ortho] 拒绝飞往 Null Island(0,0)，GPS 可能未回填', b);
      return false;
    }
    if (!(west < east)) {
      west -= 0.002;
      east += 0.002;
    }
    if (!(south < north)) {
      south -= 0.002;
      north += 0.002;
    }
    if (!(west < east && south < north)) return false;
    photoRenderBoundsRef.current = { west, south, east, north };
    const padLng = Math.max((east - west) * 0.15, 0.004);
    const padLat = Math.max((north - south) * 0.15, 0.004);
    try {
      prepareFlatMapForPhotoFly(m);
      console.log('[ortho] flyTo 航片区', {
        west, south, east, north,
        center: [(west + east) / 2, (south + north) / 2]
      });
      m.fitBounds(
        [[west - padLng, south - padLat], [east + padLng, north + padLat]],
        {
          padding: opts.padding ?? 80,
          duration: opts.duration ?? 1200,
          maxZoom: opts.maxZoom ?? 16,
          essential: true
        }
      );
      return true;
    } catch (err) {
      console.warn('[ortho] flyToPhotoRenderArea', err);
      return false;
    }
  };

  const boundsFromFeatures = (features) => {
    if (!features?.length) return null;
    const b = features.reduce((acc, f) => {
      const [lng, lat] = f.geometry.coordinates;
      return {
        west: Math.min(acc.west, lng),
        south: Math.min(acc.south, lat),
        east: Math.max(acc.east, lng),
        north: Math.max(acc.north, lat)
      };
    }, { west: 180, south: 90, east: -180, north: -90 });
    if (!(b.west < b.east)) {
      b.west -= 0.002;
      b.east += 0.002;
    }
    if (!(b.south < b.north)) {
      b.south -= 0.002;
      b.north += 0.002;
    }
    if (!(b.west < b.east && b.south < b.north)) return null;
    return b;
  };

  const paintPhotoDensity = (preview) => {
    const map = ensureMapVisibleForSelect();
    if (!map || !preview?.points?.length) return;
    if (!map.isStyleLoaded?.()) {
      message.warning('地图样式未加载完，请稍后再试密度预览');
      return;
    }
    onStartInteract?.();
    const clusters = preview.analysis?.clusters || [];
    const idToCluster = new Map();
    clusters.forEach((c) => {
      (c.assetIds || []).forEach((aid) => idToCluster.set(aid, c.id));
    });
    const features = [];
    preview.points.forEach((p) => {
      const lon = Number(p.lon);
      const lat = Number(p.lat);
      // Number(null)===0 → 非洲；必须用有效 GPS 校验
      if (!isValidWgs84Gps(lon, lat)) return;
      const mapped = toMapLngLat(lon, lat, undefined, map) || [lon, lat];
      const mlon = Number(mapped[0]);
      const mlat = Number(mapped[1]);
      if (!Number.isFinite(mlon) || !Number.isFinite(mlat)) return;
      const cid = idToCluster.get(p.id) || 0;
      features.push({
        type: 'Feature',
        properties: {
          id: p.id,
          clusterId: cid,
          include: p.includeInRender ? 1 : 0
        },
        geometry: { type: 'Point', coordinates: [mlon, mlat] }
      });
    });
    if (!features.length) {
      message.warning('没有有效 GPS 点可显示');
      return;
    }
    const fc = { type: 'FeatureCollection', features };
    clearPhotoDensityLayer();
    try {
      map.addSource(PHOTO_DENSITY_SOURCE, { type: 'geojson', data: fc });
      map.addLayer({
        id: PHOTO_DENSITY_LAYER,
        type: 'circle',
        source: PHOTO_DENSITY_SOURCE,
        paint: {
          'circle-radius': [
            'case',
            ['==', ['get', 'include'], 1], 5,
            3
          ],
          'circle-color': [
            'match',
            ['get', 'clusterId'],
            1, PHOTO_CLUSTER_COLORS[0],
            2, PHOTO_CLUSTER_COLORS[1],
            3, PHOTO_CLUSTER_COLORS[2],
            4, PHOTO_CLUSTER_COLORS[3],
            5, PHOTO_CLUSTER_COLORS[4],
            '#94a3b8'
          ],
          'circle-opacity': [
            'case',
            ['==', ['get', 'include'], 1], 0.9,
            0.35
          ],
          'circle-stroke-width': 1,
          'circle-stroke-color': '#fff'
        }
      });
    } catch (e) {
      console.error('[ortho] 密度图层添加失败', e);
      message.error('密度点叠加失败，请刷新页面后重试');
      return;
    }

    // 优先服务端主团 bounds（cluster id=1），再退回绿点包络
    // 若检测到断裂：先飞「全部团块」视野，避免只停在旧主团（如上田村）而看不到新区
    const mainCluster = clusters.find((c) => Number(c.id) === 1) || clusters[0];
    const fromServer = mapBoundsFromWgs(map, mainCluster?.bounds);
    const mainFeats = features.filter((f) => Number(f.properties.clusterId) === 1);
    const mainBounds = boundsFromFeatures(mainFeats.length ? mainFeats : features);
    const allBounds = boundsFromFeatures(features);
    const hasDiscontinuity = !!preview.analysis?.hasDiscontinuity;
    const allFromServer = mapBoundsFromWgs(map, preview.analysis?.overallBounds);
    photoRenderBoundsRef.current = hasDiscontinuity
      ? (allFromServer || allBounds || fromServer || mainBounds)
      : (fromServer || mainBounds || allBounds);
    flyToPhotoRenderArea(map, { bounds: photoRenderBoundsRef.current, duration: 1300, maxZoom: 16 });
  };

  /** 拉预览、画点、写飞行范围（可重复调用） */
  const loadPhotoPreviewData = async () => {
    if (!activeId) throw new Error('请先选择任务');
    let data = await orthoReadJson(await apiFetch(`/api/ortho/tasks/${activeId}/photo-preview`));
    if (data.withGps === 0 && data.totalAssets > 0) {
      message.info('素材尚无 GPS，正在从 OSS 回填 EXIF…');
      const bf = await orthoReadJson(await apiFetch(`/api/ortho/tasks/${activeId}/backfill-gps`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ limit: 500 })
      }));
      message.success(bf.message || `回填 ${bf.updated} 张`);
      data = await orthoReadJson(await apiFetch(`/api/ortho/tasks/${activeId}/photo-preview`));
    }
    if (!data.withGps) {
      throw new Error('仍无可用 GPS，无法密度预览/框选（请确认航片含 EXIF GPS）');
    }
    paintPhotoDensity(data);
    return data;
  };

  /**
   * 进入渲染区框选（可反复重选）。
   * 自动补密度预览 + 飞到主团/上次选区，不依赖「只能点一次」。
   */
  const startRenderAoiSelect = async ({ skipModal = false } = {}) => {
    if (!activeId) return;
    // 框选需看清地图，先收起管理弹窗（任务状态仍保留）
    setOrthoManageOpen(false);
    const map = ensureMapVisibleForSelect();
    if (!map || !mapReady) {
      message.warning('地图未就绪，请等底图加载后再框选；若仍空白请 Ctrl+F5 硬刷新');
      return;
    }
    setBusy(true);
    try {
      // 整段会话加锁：弹窗期间也不能被「点空白飞设备」抢走（含 Modal 点穿）
      beginAoiInteract();
      onStartInteract?.();
      prepareFlatMapForPhotoFly(map);
      // 结束上一轮框选残留，保证 useEffect 能重新挂上
      setSelectMode(null);
      clearBoxPreview();
      // 丢弃上次任务留下的飞行范围，避免飞回旧村庄
      photoRenderBoundsRef.current = null;
      try {
        map.dragPan?.enable?.();
      } catch (_) { /* ignore */ }

      const data = await loadPhotoPreviewData();
      const analysis = data.analysis || {};
      // 以服务端最新任务为准（避免准备重建后前端仍持有旧 renderAoi）
      const freshAoi = data.task?.renderAoi || activeTask?.renderAoi;
      const alreadySelected = !!freshAoi?.confirmedAt;
      const flyBounds = photoRenderBoundsRef.current;

      const enterSelect = () => {
        beginAoiInteract();
        // 密度预览已 fly 过；再飞一次盖掉可能被点穿触发的设备定位（强制用本次预览 bounds）
        setTimeout(() => {
          flyToPhotoRenderArea(map, { bounds: flyBounds || photoRenderBoundsRef.current, duration: 1500, maxZoom: 16 });
          beginBboxSelect('render-aoi');
        }, 50);
      };

      if (skipModal || alreadySelected) {
        // 再框选：不再弹第二次大确认，直接飞过去框
        message.info(alreadySelected
          ? '可重新框选：新框将覆盖上次选区'
          : '请拖拽框选要渲染的村庄主区');
        enterSelect();
        return;
      }

      Modal.confirm({
        title: analysis.hasDiscontinuity ? '检测到拍摄区域断裂' : '航片密度预览',
        width: 520,
        content: (
          <div>
            <p>{analysis.message}</p>
            <p className="zhxt-ortho-muted">
              有 GPS {data.withGps}/{data.totalAssets} 张；地图上按团块着色（绿=主团，橙/红=断裂团）。
              请框选<strong>只要渲染的村庄区域</strong>，框外照片将不参与建模。可多次重选。
              {analysis.hasDiscontinuity ? ' 地图已缩放到全部团块；若混入旧场景航片，请先点「清空旧航片」。' : ''}
            </p>
          </div>
        ),
        okText: '鼠标框选渲染区',
        cancelText: analysis.hasDiscontinuity ? '暂不处理' : '关闭',
        onOk: () => enterSelect(),
        onCancel: () => endAoiInteractSoon(200)
      });
    } catch (e) {
      message.error(e.message || '预览失败');
      endAoiInteractSoon(0);
    } finally {
      setBusy(false);
    }
  };

  /** 开算前：密度预览 + 框选（兼容旧调用名） */
  const openPhotoPreview = () => startRenderAoiSelect({ skipModal: false });

  const confirmRenderAoiBbox = async (wgs) => {
    if (!activeId) throw new Error('请先选择任务');
    const data = await orthoReadJson(await apiFetch(`/api/ortho/tasks/${activeId}/render-aoi`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        west: wgs.west,
        south: wgs.south,
        east: wgs.east,
        north: wgs.north
      })
    }));
    message.success(data.message || '已保存渲染选区');
    await loadTasks();
    try {
      const preview = await orthoReadJson(await apiFetch(`/api/ortho/tasks/${activeId}/photo-preview`));
      paintPhotoDensity(preview);
    } catch (_) { /* ignore */ }
  };

  return {
    photoRenderBoundsRef,
    clearPhotoDensityLayer,
    prepareFlatMapForPhotoFly,
    ensureMapVisibleForSelect,
    flyToPhotoRenderArea,
    paintPhotoDensity,
    startRenderAoiSelect,
    openPhotoPreview,
    confirmRenderAoiBbox
  };
}
